import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  IconButton,
  Box,
  Divider,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import WorkIcon from "@mui/icons-material/Work";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LanguageButton from "components/button/LanguageButton";
import PhoneIcon from "@mui/icons-material/Phone";
import logo from "../../images/logo/ncc-logo.png";

import tw from "twin.macro";
import styled from "styled-components";

export const NavLink = tw.a`
  text-lg my-2 lg:text-sm lg:mx-6 lg:my-0
  font-semibold tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-primary-500 hocus:text-primary-500
`;

export const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 text-2xl! ml-0!`};

  img {
    ${tw`w-10 mr-3`}
  }
`;

const defaultLogoLink = (
  <LogoLink href="/">
    <img style={{ height: "40px", width: "auto" }} src={logo} alt="logo" />
  </LogoLink>
);

const MobileDrawer = ({ navLinks }) => {
  const [open, setOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const { t } = useTranslation();

  const menuItems = [
    {
      title: t("header_nav_service"),
      icon: <i className="fa-solid fa-network-wired"></i>,
      path: "/it-service",
    },
    {
      title: t("header_nav_business"),
      icon: <i className="fa-solid fa-user-tie"></i>,
      path: "/business",
      subItems: [
        {
          title: t("business.ses.heading"),
          path: "/business#system-engineering",
        },
        {
          title: t("business.reinforcement.heading"),
          path: "/business#reinforcement-system",
        },
        {
          title: t("business.lease-of-pc.heading"),
          path: "/business#lease-business",
        },
        {
          title: t("business.chineseMarketSupport.heading"),
          path: "/business#chinese-market",
        },
      ],
    },
    {
      title: t("header_nav_aboutus"),
      icon: <InfoIcon />,
      path: "/company",
      subItems: [
        {
          title: t("submenu_aboutus_profile"),
          path: "/company#profile",
        },
        {
          title: t("submenu_aboutus_location"),
          path: "/company#location",
        },
        {
          title: t("submenu_aboutus_boardMember"),
          path: "/company#board-member",
        },
        {
          title: t("submenu_aboutus_partner"),
          path: "/company#suppliers",
        },
        {
          title: t("submenu_aboutus_history"),
          path: "/company#history",
        },
      ],
    },
    {
      title: t("header_nav_recruitment"),
      icon: <WorkIcon />,
      path: "/recruitment",
      subItems: [
        {
          title: t("submenu_recruitment_jobPostings"),
          path: "/recruitment#job-postings",
        },
        {
          title: t("submenu_recruitment_faq"),
          path: "/recruitment#job-hunting-faq",
        },
        {
          title: t("submenu_recruitment_training"),
          path: "/recruitment#training-programs",
        },
      ],
    },
    {
      title: t("header_nav_contactus"),
      icon: <PhoneIcon />,
      path: "/contact",
    },
  ];

  const handleMenuClick = (menuTitle) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuTitle]: !prev[menuTitle],
    }));
  };

  const handleNavigation = (path) => {
    window.location.href = path;
    setOpen(false);
  };

  return (
    <>
      <IconButton onClick={() => setOpen(true)} sx={{ color: "white" }}>
        <MenuIcon />
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: "85%",
            maxWidth: "360px",
            bgcolor: "#f9f9f9",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#0c4d96",
            color: "white",
          }}
        >
          {defaultLogoLink}
          <IconButton onClick={() => setOpen(false)} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Navigation List */}
        <List sx={{ pt: 0, flexGrow: 1 }}>
          {menuItems.map((item, index) => (
            <React.Fragment key={index}>
              <ListItem
                sx={{
                  py: 1.5,
                  px: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {/* Clickable Text */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexGrow: 1,
                    cursor: "pointer",
                  }}
                  onClick={() => handleNavigation(item.path)}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      fontSize: "0.95rem",
                      fontWeight: 500,
                    }}
                  />
                </Box>

                {/* Toggle Button */}
                {item.subItems && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent event bubbling
                      handleMenuClick(item.title);
                    }}
                    sx={{ p: 0 }}
                  >
                    {expandedMenus[item.title] ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </IconButton>
                )}
              </ListItem>

              {item.subItems && (
                <Collapse
                  in={expandedMenus[item.title]}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem, subIndex) => (
                      <ListItem
                        key={subIndex}
                        button
                        onClick={() => handleNavigation(subItem.path)}
                        sx={{
                          pl: 7,
                          py: 1,
                          bgcolor: "grey.50",
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <ChevronRightIcon sx={{ fontSize: "1.2rem" }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={subItem.title}
                          primaryTypographyProps={{
                            fontSize: "0.85rem",
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
              {index < menuItems.length - 1 && <Divider />}
            </React.Fragment>
          ))}

          <Box display={"flex"} justifyContent={"center"} mt={2}>
            <LanguageButton />
          </Box>
        </List>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            textAlign: "center",
            bgcolor: "#f5f5f5",
          }}
        >
          <Typography fontWeight={550} fontSize={"0.7rem"} variant="subtitle2">
            {t("footer_copyright", { year: new Date().getFullYear() })}
          </Typography>
        </Box>
      </Drawer>
    </>
  );
};

export default MobileDrawer;
